import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MessageSquare, X, Trash2, AlertTriangle, ExternalLink, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const AUDIENCE_LABELS = {
  institution: "Institution", organization: "Organization", professional: "Professional", verifier: "Verifier", general: "General",
};
const STATUS_OPTS = ["new", "in_progress", "contacted", "won", "lost", "archived"];
const STATUS_COLORS = {
  new: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  in_progress: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  contacted: "bg-violet-500/20 text-violet-300 border-violet-500/40",
  won: "bg-ec-emerald/20 text-ec-emerald border-ec-emerald/40",
  lost: "bg-ec-error/20 text-ec-error border-ec-error/40",
  archived: "bg-zinc-700 text-zinc-400 border-zinc-600",
};

export default function AdminInquiries() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [audience, setAudience] = useState("");
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/inquiries", { params: { q, audience, status, skip, limit: 50 } });
      setRows(data.rows); setCounts(data.counts || {}); setTotal(data.total);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [q, audience, status, skip]);
  useEffect(() => { load(); }, [load]);

  const reset = (val, setter) => { setter(val); setSkip(0); };

  return (
    <AdminLayout
      subtitle="Module 27"
      title="Inquiries & Demo Requests"
      actions={<div className="text-xs font-mono-plex text-zinc-500">{total.toLocaleString()} inquiries</div>}
    >
      <div className="flex flex-wrap gap-2 mb-5" data-testid="inq-filter-chips">
        <Chip label={`All · ${counts.all || 0}`} active={!status} onClick={() => reset("", setStatus)} testid="inq-chip-all" />
        {STATUS_OPTS.map((s) => (
          <Chip key={s} label={`${s.replace("_", " ")} · ${counts[s] || 0}`} active={status === s} onClick={() => reset(s, setStatus)} testid={`inq-chip-${s}`} />
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => reset(e.target.value, setQ)} placeholder="Search name, email, org, message…" className="rounded-sm h-9 pl-9 bg-white/5 border-white/10 text-white" data-testid="inq-search" />
        </div>
        <Select value={audience || "all"} onValueChange={(v) => reset(v === "all" ? "" : v, setAudience)}>
          <SelectTrigger className="w-[180px] rounded-sm h-9 bg-white/5 border-white/10 text-white" data-testid="inq-audience-trigger"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All audiences</SelectItem>
            {Object.entries(AUDIENCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="inq-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <th className="px-4 py-3 eyebrow text-zinc-500">Lead</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Audience</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Status</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Submitted</th>
                <th className="px-4 py-3 eyebrow text-zinc-500 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="p-10 text-center text-zinc-500" data-testid="inq-empty">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  No inquiries match these filters yet.
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`inq-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-white">{r.full_name}</div>
                    <div className="text-xs text-zinc-500">{r.email}</div>
                    {r.organization_name && <div className="text-[10px] font-mono-plex text-zinc-600">{r.organization_name}</div>}
                  </td>
                  <td className="px-4 py-3"><Badge className="rounded-sm bg-white/10 border-white/10 text-white">{AUDIENCE_LABELS[r.audience] || r.audience}</Badge></td>
                  <td className="px-4 py-3"><Badge className={`rounded-sm border ${STATUS_COLORS[r.status] || ""}`}>{r.status}</Badge></td>
                  <td className="px-4 py-3 text-xs font-mono-plex text-zinc-500">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSelected(r)} className="p-1.5 rounded-sm border border-white/10 hover:bg-white/10" data-testid={`inq-open-${r.id}`}>
                      <ChevronRight className="w-3 h-3 text-zinc-300" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {selected && <InquiryDrawer inquiry={selected} onClose={() => setSelected(null)} onUpdated={load} />}
    </AdminLayout>
  );
}

function Chip({ label, active, onClick, testid }) {
  return (
    <button onClick={onClick} className={`px-3 py-1.5 text-xs rounded-sm border ${active ? "bg-ec-emerald text-ec-ink border-ec-emerald" : "border-white/10 text-zinc-400 hover:bg-white/5"}`} data-testid={testid}>
      {label}
    </button>
  );
}

function InquiryDrawer({ inquiry, onClose, onUpdated }) {
  const [data, setData] = useState(inquiry);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState(inquiry.status);
  const [priority, setPriority] = useState(inquiry.priority || "normal");
  const [assigned, setAssigned] = useState(inquiry.assigned_to || "");

  const save = async (partial) => {
    try {
      await api.patch(`/admin/inquiries/${data.id}`, partial);
      const fresh = (await api.get(`/admin/inquiries/${data.id}`)).data;
      setData(fresh);
      onUpdated();
      toast.success("Updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await save({ notes_append: note });
    setNote("");
  };

  const del = async () => {
    if (!confirm("Delete this inquiry permanently?")) return;
    try {
      await api.delete(`/admin/inquiries/${data.id}`);
      toast.success("Deleted");
      onUpdated();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex justify-end" onClick={onClose} data-testid="inq-drawer">
      <div className="bg-ec-ink border-l border-white/10 w-full max-w-xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-ec-ink border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="eyebrow text-ec-emerald">{AUDIENCE_LABELS[data.audience]}</div>
            <h3 className="font-display text-lg font-bold text-white">{data.full_name}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="inq-drawer-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-5 text-sm">
          <section className="space-y-1.5">
            <KV label="Email" value={<a href={`mailto:${data.email}`} className="text-ec-emerald hover:underline inline-flex items-center gap-1">{data.email} <ExternalLink className="w-3 h-3" /></a>} />
            <KV label="Phone" value={data.phone || "—"} />
            <KV label="Organization" value={data.organization_name || "—"} />
            <KV label="Country" value={data.country || "—"} />
            <KV label="Role title" value={data.role_title || "—"} />
            <KV label="Team / strength" value={data.team_size || "—"} />
            <KV label="Source" value={<span className="font-mono-plex text-xs">{data.source_path || "—"}</span>} />
            <KV label="Submitted" value={<span className="font-mono-plex text-xs">{(data.created_at || "").slice(0, 19).replace("T", " ")}</span>} />
          </section>

          {data.message && (
            <section className="border border-white/10 bg-white/5 p-3 rounded-sm">
              <div className="eyebrow text-zinc-500 mb-2">Message</div>
              <p className="text-zinc-200 whitespace-pre-wrap text-sm">{data.message}</p>
            </section>
          )}

          <section className="grid grid-cols-3 gap-2" data-testid="inq-drawer-controls">
            <DrawerSelect label="Status" value={status} setValue={(v) => { setStatus(v); save({ status: v }); }} options={STATUS_OPTS} testid="inq-status-select" />
            <DrawerSelect label="Priority" value={priority} setValue={(v) => { setPriority(v); save({ priority: v }); }} options={["low", "normal", "high"]} testid="inq-priority-select" />
            <div>
              <div className="eyebrow text-zinc-500 mb-1">Assigned</div>
              <Input value={assigned} onChange={(e) => setAssigned(e.target.value)} onBlur={() => save({ assigned_to: assigned || null })} placeholder="admin@…" className="rounded-sm h-8 bg-white/5 border-white/10 text-white text-xs" data-testid="inq-assigned-input" />
            </div>
          </section>

          <section>
            <div className="eyebrow text-zinc-500 mb-2">Notes ({(data.notes || []).length})</div>
            <ul className="space-y-2 mb-3" data-testid="inq-notes-list">
              {(data.notes || []).map((n, i) => (
                <li key={`${n.at || ""}-${n.by || ""}-${i}`} className="border border-white/10 bg-white/5 p-2 rounded-sm text-xs">
                  <div className="text-zinc-200">{n.text}</div>
                  <div className="text-zinc-500 mt-1 font-mono-plex">— {n.by} · {n.at?.slice(0, 16).replace("T", " ")}</div>
                </li>
              ))}
              {(data.notes || []).length === 0 && <li className="text-zinc-500 text-xs italic">No notes yet.</li>}
            </ul>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add an internal note…" className="rounded-sm bg-white/5 border-white/10 text-white text-sm" data-testid="inq-note-input" />
            <Button onClick={addNote} size="sm" disabled={!note.trim()} className="rounded-sm mt-2 bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink" data-testid="inq-note-add">Add note</Button>
          </section>

          <section className="border-t border-white/10 pt-4 flex justify-between">
            <Button variant="outline" size="sm" onClick={del} className="rounded-sm border-ec-error/30 text-ec-error hover:bg-ec-error/10 gap-1.5" data-testid="inq-delete">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </Button>
            {data.priority === "high" && <div className="flex items-center gap-1 text-xs text-ec-error"><AlertTriangle className="w-3.5 h-3.5" /> High priority</div>}
          </section>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex items-start gap-3 py-1.5 border-b border-white/5">
      <div className="w-28 eyebrow text-zinc-500 text-[10px]">{label}</div>
      <div className="flex-1 text-zinc-200 break-words">{value}</div>
    </div>
  );
}
function DrawerSelect({ label, value, setValue, options, testid }) {
  return (
    <div>
      <div className="eyebrow text-zinc-500 mb-1 text-[10px]">{label}</div>
      <select value={value} onChange={(e) => setValue(e.target.value)} className="w-full rounded-sm h-8 px-2 bg-white/5 border border-white/10 text-white text-xs" data-testid={testid}>
        {options.map((o) => <option key={o} value={o} className="bg-ec-ink">{o.replace("_", " ")}</option>)}
      </select>
    </div>
  );
}
